import { Moment } from '@chipinside/frontend'
import { Translate } from '@chipinside/translate'
import { Box, Divider } from '@mui/material'
import { Button } from '@mui/material'
import { isEmpty } from 'lodash'
import { Link } from 'react-router-dom'

import { CComStatus } from '#/@types/backend'
import BooleanBadge from '#/components/badges/BooleanBadge'
import CowmedChip from '#/components/Chip'
import { Drawer } from '#/components/Drawer'
import FormatCell from '#/components/FormatCell'
import { Icon } from '#/components/Icon'
import { InfoCard } from '#/components/Maps/components/DrawerFetch/card'
import { TextPair } from '#/components/Maps/components/DrawerFetch/text'
import { useFarmSlug } from '#/store/ducks/farm'
import { usePermissions } from '#/store/ducks/permissions'
import { useFormBuilder } from '#/store/hooks/useFormBuilder'
import { listeners } from '#/utils/listeners'
import { numberDisplay } from '#/utils/numberDisplay'

type CComData = {
  serial: number
  batches: { name: string; slug: string }[]
  code_prefix: string
  code: number
  has_sync_last_configuration: boolean
  primary: boolean
  active: boolean
  last_config_confirmation: string | null
  last_config_request: string | null
  last_connection: string | null
  status: CComStatus
  last_primary_status: {
    sbc_version: string | null
    mac: string | null
  }
  unit: {
    farm: {
      code: number
      name: string
      slug: string
    }
  }
  configuration: {
    latitude: number
    longitude: number
    use_location: boolean
  }
}

const getCComStatus = ({ status }) => {
  const statuses = {
    inactive: {
      status,
      text: Translate({ messageKey: 'inactive_male' }),
    },
    offline: {
      status,
      text: Translate({ messageKey: 'offline' }),
    },
    operational: {
      status,
      text: Translate({ messageKey: 'operational' }),
    },
    waiting: {
      status,
      text: Translate({ messageKey: 'waiting' }),
    },
  }

  return statuses[status]
}

export default function CComDrawer({ ccom, data }: { data?: CComData; ccom?: any }) {
  const ccom_status = getCComStatus({ status: ccom?.status_for_map ?? data?.status })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 2 }}>
      <InfoCard
        title={Translate({ messageKey: 'general_info_plural' })}
        icon={<Icon name="file-text" size={20} />}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <TextPair label={Translate({ messageKey: 'serial' })} value={data?.serial} />
          <TextPair
            label={Translate({ messageKey: 'farm' })}
            value={`${data?.unit?.farm?.code} - ${data?.unit?.farm?.name}`}
          />

          {/* TOPOLOGIA */}
          {data?.primary ? (
            <TextPair
              label={Translate({ messageKey: 'type' })}
              value={
                <CowmedChip
                  variant="soft"
                  value={Translate({ messageKey: 'primary' })}
                  width="unset"
                  size="small"
                />
              }
            />
          ) : (
            <TextPair
              label={Translate({ messageKey: 'type' })}
              value={
                <CowmedChip
                  variant="soft"
                  value={Translate({ messageKey: 'secondary' })}
                  width="unset"
                  size="small"
                />
              }
            />
          )}

          {/* Lotes relacionados */}
          <TextPair
            label={Translate({ messageKey: 'related_batches' })}
            justifyBetween={isEmpty(data?.batches)}
            value={
              !isEmpty(data?.batches) ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {data?.batches.map(batch => (
                    <CowmedChip
                      key={batch.slug}
                      variant="outlined"
                      value={batch.name}
                      width="unset"
                      size="small"
                    />
                  ))}
                </Box>
              ) : (
                '-'
              )
            }
          />

          <Divider sx={{ my: 1 }} />

          <TextPair
            label={Translate({ messageKey: 'topology' })}
            value={`${data?.code_prefix} ${data?.code}`}
          />
          <TextPair
            label={Translate({ messageKey: 'SBC_version' })}
            value={data?.last_primary_status?.sbc_version ?? 'N/A'}
          />
          <TextPair
            label={Translate({ messageKey: 'mac_address' })}
            value={data?.last_primary_status?.mac ?? 'N/A'}
          />
        </Box>
      </InfoCard>

      <InfoCard
        title={Translate({ messageKey: 'status' })}
        icon={<Icon name="cloud-check" size={20} />}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* STATUS */}
          <TextPair
            label={Translate({ messageKey: 'status' })}
            value={<FormatCell type="ccom_status" value={ccom_status} />}
          />

          {/* RECEPÇÃO */}
          <TextPair
            label={Translate({ messageKey: 'reception' })}
            value={
              data?.active ? (
                <CowmedChip
                  variant="soft"
                  icon={<Icon name="check" size={14} />}
                  value={Translate({ messageKey: 'active_male' })}
                  level="success"
                  width="unset"
                  size="small"
                />
              ) : (
                <CowmedChip
                  variant="soft"
                  icon={<Icon name="x" size={14} />}
                  value={Translate({ messageKey: 'inactive_male' })}
                  level="error"
                  width="unset"
                  size="small"
                />
              )
            }
          />

          {/* SYNCRONIZADO */}
          <TextPair
            label={Translate({ messageKey: 'synchronized' })}
            value={
              data?.has_sync_last_configuration ? (
                <CowmedChip
                  variant="soft"
                  icon={<Icon name="check" size={14} />}
                  value={Translate({ messageKey: 'synchronized' })}
                  level="success"
                  size="small"
                  width="unset"
                />
              ) : (
                <CowmedChip
                  variant="soft"
                  icon={<Icon name="x" size={14} />}
                  value={Translate({ messageKey: 'desynchronized' })}
                  level="error"
                  size="small"
                  width="unset"
                />
              )
            }
          />

          <Divider sx={{ my: 1 }} />

          <TextPair
            label={Translate({ messageKey: 'last_request' })}
            value={Moment.view(data?.last_config_request) ?? 'N/A'}
          />
          <TextPair
            label={Translate({ messageKey: 'last_connection' })}
            value={Moment.view(data?.last_connection) ?? 'N/A'}
          />
          <TextPair
            label={Translate({ messageKey: 'last_confirmation' })}
            value={Moment.view(data?.last_config_confirmation) ?? 'N/A'}
          />
        </Box>
      </InfoCard>

      <InfoCard
        title={Translate({ messageKey: 'coordinates' })}
        icon={<Icon name="map-pin" size={20} />}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <TextPair
            label={Translate({ messageKey: 'latitude' })}
            value={numberDisplay(data?.configuration?.latitude)}
          />
          <TextPair
            label={Translate({ messageKey: 'longitude' })}
            value={numberDisplay(data?.configuration?.longitude)}
          />
          <TextPair
            label={Translate({ messageKey: 'uses_location' })}
            value={<BooleanBadge value={data?.configuration?.use_location} />}
          />
        </Box>
      </InfoCard>
    </Box>
  )
}

export function CComFooter({ marker }) {
  const { build } = useFormBuilder()
  const permissions = usePermissions()
  const farmSlug = useFarmSlug()

  function updateMarker() {
    const serial = marker?.slug

    Drawer.close()
    build({
      afterSuccess: () => window.dispatchEvent(new Event(listeners.ccommap)),
      formName: 'ccom',
      formType: 'config',
      params: { serial },
    })
  }

  function attach_batch() {
    Drawer.close()
    build({
      formName: 'farm_ccom',
      formType: 'batch_attach',
      params: {
        code: marker?.code,
        farm: farmSlug,
        serial: marker?.serial,
      },
    })
  }
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      {farmSlug && permissions?.farm?.manage_ccom_batch && (
        <Button variant="outlined" fullWidth onClick={attach_batch}>
          {Translate({
            messageKey: 'attach_item',
            params: {
              item: Translate({ lower: true, messageKey: 'batch_plural' }),
            },
          })}
        </Button>
      )}
      {permissions?.system?.list_ccom && (
        <Button
          variant="outlined"
          fullWidth
          component={Link}
          to={`/system/inventory/c-com/${marker.slug}/status`}
          onClick={() => Drawer.close()}
        >
          {Translate({
            messageKey: 'access_item',
            params: { item: Translate({ messageKey: 'c_com' }) },
          })}
        </Button>
      )}
      {permissions?.system?.config_ccom && (
        <Button variant="outlined" fullWidth onClick={updateMarker}>
          {Translate({ messageKey: 'config_item' })}
        </Button>
      )}
    </Box>
  )
}
